import { useEffect, useMemo, useState } from "react";
import { Plus, Edit2, Trash2, PlayCircle, Loader2, KeyRound, Phone, MessageSquare, Brain, Building, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useCurrentModulePerms } from "@/contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const CATEGORIES = [
  { v: "voice",     label: "Voice / SIP",       icon: Phone },
  { v: "messaging", label: "Messaging",         icon: MessageSquare },
  { v: "ai",        label: "AI / Speech",       icon: Brain },
  { v: "crm",       label: "CRM",               icon: Building },
  { v: "billing",   label: "Billing",           icon: CreditCard },
];

export default function IntegrationsVault() {
  const [providers, setProviders] = useState({});
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("voice");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { canWrite, canDelete } = useCurrentModulePerms();

  async function load() {
    setLoading(true);
    try {
      const [p, i] = await Promise.all([api.get("/admin/integrations/providers"), api.get("/admin/integrations")]);
      setProviders(p.data || {}); setItems(i.data || []);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startNew(category) {
    const list = providers[category] || [];
    const provider = list[0]?.provider || "";
    setForm({ category, provider, label: list[0]?.label || provider, config: {}, secrets: {}, is_enabled: true });
    setEditing("new");
  }
  function startEdit(it) {
    setForm({
      category: it.category, provider: it.provider, label: it.label || it.provider,
      config: { ...(it.config || {}) },
      secrets: {}, // empty; user re-enters or leaves blank to keep
      is_enabled: !!it.is_enabled,
      _existing_secrets: it.secret_keys || [],
      _masked: it.secrets_masked || {},
    });
    setEditing(it);
  }
  function providerSchema(category, provider) {
    return (providers[category] || []).find((p) => p.provider === provider);
  }
  async function save() {
    setSaving(true);
    try {
      const schema = providerSchema(form.category, form.provider);
      const config = { ...(form.config || {}) };
      const secrets = {};
      if (form.secrets) for (const [k, v] of Object.entries(form.secrets)) if (v !== "") secrets[k] = v;
      if (editing === "new") {
        await api.post("/admin/integrations", {
          category: form.category, provider: form.provider, label: form.label || form.provider,
          config, secrets, is_enabled: !!form.is_enabled,
        });
      } else {
        await api.patch(`/admin/integrations/${editing.id}`, {
          label: form.label, config, secrets: Object.keys(secrets).length ? secrets : undefined, is_enabled: !!form.is_enabled,
        });
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  }
  async function remove(it) {
    if (!confirm(`Delete ${it.label}?`)) return;
    try { await api.delete(`/admin/integrations/${it.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }
  async function test(it) {
    try { const r = await api.post(`/admin/integrations/${it.id}/test`); (r.data?.status === "ok" ? toast.success : toast.error)(r.data?.detail || "Tested"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  }

  const grouped = useMemo(() => CATEGORIES.map((c) => ({ ...c, items: items.filter((i) => i.category === c.v), templates: providers[c.v] || [] })), [items, providers]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="vault-title">Integrations Vault</h1>
          <p className="text-sm text-muted-foreground mt-1">Add API credentials for SIP, channels, AI, CRM, and Stripe. Secrets are encrypted at rest with Fernet — only masked previews are shown after save.</p>
        </div>
      </div>
      <Card className="p-2">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {grouped.map((c) => (
              <TabsTrigger key={c.v} value={c.v} data-testid={`vault-tab-${c.v}`}>
                <c.icon className="w-3.5 h-3.5 mr-1.5" />{c.label}
                <Badge variant="outline" className="ml-2 text-[10px]">{c.items.length}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
          {grouped.map((c) => (
            <TabsContent key={c.v} value={c.v} className="mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">Available providers: {c.templates.map((t) => t.label).join(" · ")}</div>
                {canWrite && <Button data-testid={`add-int-${c.v}`} onClick={() => startNew(c.v)} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"><Plus className="w-4 h-4" /> Add {c.label}</Button>}
              </div>
              {loading ? <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
                <div className="grid md:grid-cols-2 gap-3">
                  {c.items.map((it) => (
                    <Card key={it.id} className="p-4" data-testid={`int-card-${it.id}`}>
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-md bg-muted grid place-items-center"><KeyRound className="w-4 h-4" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{it.label}</span>
                            {it.is_enabled ? <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/15 text-[10px]">Enabled</Badge> : <Badge variant="outline" className="text-[10px]">Disabled</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">{it.provider}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button data-testid={`int-test-${it.id}`} onClick={()=>test(it)} variant="ghost" size="icon" className="h-8 w-8" title="Test"><PlayCircle className="w-4 h-4" /></Button>
                          {canWrite && <Button data-testid={`int-edit-${it.id}`} onClick={()=>startEdit(it)} variant="ghost" size="icon" className="h-8 w-8"><Edit2 className="w-4 h-4" /></Button>}
                          {canDelete && <Button data-testid={`int-del-${it.id}`} onClick={()=>remove(it)} variant="ghost" size="icon" className="h-8 w-8 text-destructive"><Trash2 className="w-4 h-4" /></Button>}
                        </div>
                      </div>
                      {it.secret_keys?.length > 0 && (
                        <div className="mt-3 grid grid-cols-2 gap-1 text-[11px]">
                          {it.secret_keys.map((k) => (
                            <div key={k} className="flex items-center justify-between p-1.5 rounded bg-muted/40">
                              <code className="truncate">{k}</code>
                              <code className="text-muted-foreground">{it.secrets_masked?.[k] || "•••"}</code>
                            </div>
                          ))}
                        </div>
                      )}
                      {it.last_test_status && (
                        <div className={`mt-2 text-[11px] ${it.last_test_status==="ok"?"text-emerald-600 dark:text-emerald-400":"text-destructive"}`}>
                          Last test: {it.last_test_status} — {new Date(it.last_test_at).toLocaleString()}
                        </div>
                      )}
                    </Card>
                  ))}
                  {c.items.length === 0 && <div className="md:col-span-2 py-12 text-center text-sm text-muted-foreground">No {c.label.toLowerCase()} integrations yet.</div>}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v)=>!v&&setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing==="new"?`Add ${form.category} integration`:`Edit ${editing?.label}`}</DialogTitle>
            <DialogDescription>Secrets are encrypted at rest — leave fields blank to keep existing values.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Label</Label><Input data-testid="int-label" value={form.label||""} onChange={(e)=>setForm({...form, label:e.target.value})} /></div>
            {editing==="new" && (
              <div><Label>Provider</Label>
                <Select value={form.provider} onValueChange={(v)=>{const p=providerSchema(form.category, v); setForm({...form, provider:v, label: p?.label || v, config: {}, secrets: {}});}}>
                  <SelectTrigger data-testid="int-provider" className="h-9"><SelectValue/></SelectTrigger>
                  <SelectContent>{(providers[form.category]||[]).map((p)=><SelectItem key={p.provider} value={p.provider}>{p.label}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            <div className="flex items-center gap-2"><Switch data-testid="int-enabled" checked={!!form.is_enabled} onCheckedChange={(v)=>setForm({...form, is_enabled:!!v})} /><Label>Enabled</Label></div>
            {(() => {
              const sch = providerSchema(form.category, form.provider);
              if (!sch) return <div className="text-xs text-muted-foreground">Pick a provider…</div>;
              return (
                <>
                  {sch.fields.public.length > 0 && <div className="text-overline text-muted-foreground pt-2">Public fields</div>}
                  {sch.fields.public.map((k) => (
                    <div key={k}><Label>{k}</Label><Input data-testid={`int-config-${k}`} value={form.config?.[k]||""} onChange={(e)=>setForm({...form, config:{...form.config, [k]:e.target.value}})} /></div>
                  ))}
                  {sch.fields.secrets.length > 0 && <div className="text-overline text-muted-foreground pt-2">Secrets (encrypted)</div>}
                  {sch.fields.secrets.map((k) => (
                    <div key={k}>
                      <Label>{k} {form._masked?.[k] ? <span className="text-[10px] text-muted-foreground ml-2">current: {form._masked[k]}</span> : null}</Label>
                      <Input data-testid={`int-secret-${k}`} type="password" placeholder={form._masked?.[k] ? "Leave blank to keep" : ""} value={form.secrets?.[k]||""} onChange={(e)=>setForm({...form, secrets:{...form.secrets, [k]:e.target.value}})} />
                    </div>
                  ))}
                </>
              );
            })()}
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setEditing(null)}>Cancel</Button><Button data-testid="int-save" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-primary-foreground">{saving?<Loader2 className="w-4 h-4 animate-spin"/>:"Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
